import "./AddApplication.scss";

import React, { memo, useEffect, useState } from "react";
import { Form } from "react-bootstrap";

import { CustomInput } from "../../../../../components/CustomInput/CustomInput";
import { CustomModal } from "../../../../../components/CustomModal";
import { useOrganizationContext } from "../../../../../contexts/organizations/organizationsContext";
import { useCreateApplication } from "../../../../../hooks/reactQueryHooks/useApplications";
import { useCheckAccess } from "../../../../../hooks/useCheckAccess";
import { AddApplicationFooter } from "./AddApplicationFooter";

interface IProps {
  show: boolean;
  handleClose: () => void;
}

const AddApplicationModal: React.FC<IProps> = memo(({ show, handleClose }) => {
  const [name, setName] = useState<string>("");
  const [threshold, setThreshold] = useState<number>(0.9);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setThreshold(Number(e.target.value));
  };

  const createMutation = useCreateApplication();

  const { currentOrganization } = useOrganizationContext();

  const noSubscriptionError = `Creating projects is disabled while there is no active subscription for the ${currentOrganization?.name} organization.`;

  const { displayError } = useCheckAccess(
    noSubscriptionError,
    createMutation.isError,
  );

  const addApplication = () => {
    createMutation.mutate({
      id: currentOrganization ? currentOrganization.id : "",
      name,
      settings: {
        similarityThreshold: 1 - threshold,
      },
    });
  };

  useEffect(() => {
    if (createMutation.isSuccess) {
      handleClose();
    }
  }, [createMutation, handleClose]);

  const closePopup = () => {
    handleClose();
  };

  return (
    <CustomModal
      show={show}
      handleClose={closePopup}
      title="Add project"
      footer={
        <AddApplicationFooter
          disabledSubmit={
            name.trim().length === 0 ||
            createMutation.isLoading ||
            !!displayError
          }
          onCancel={handleClose}
          addApplication={addApplication}
          isProcessing={createMutation.isLoading}
        />
      }
      errorMessage={displayError}
    >
      <div className="custom-textarea">
        <CustomInput
          label="Name"
          handleChange={handleChange}
          inputValue={name}
          placeholder="Enter the project name"
          maxLength={150}
          autoFocus
          required
        />
      </div>
      <div className="py-2" />
      <div className="custom-textarea">
        <label
          className="d-block fs-7 mt-0 form-label"
          htmlFor="similarity-threshold"
        >
          Similarity threshold
        </label>

        <div className="d-flex justify-content-space-between">
          <Form.Range
            min={0}
            max={0.99}
            step={0.01}
            value={threshold}
            onChange={handleThresholdChange}
            id="similarity-threshold"
          />
          <span className="range-value">{Math.round(threshold * 100)}%</span>
        </div>
        <div className="short-explanation w-100">
          Set the percentage to determine how closely a cache item must match
          the prompt. Higher values mean more precise matches with less risk of
          returning inadequate responses.
        </div>
      </div>
    </CustomModal>
  );
});

export { AddApplicationModal };
